import { useRef, useState } from 'react'
import { Modal, Button, Input } from '../ui'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import SignaturePad, { type SignaturePadHandle } from './SignaturePad'
import { initialsFrom, INK, SIGNATURE_FONT, typedToPng } from './signatureImage'

export interface AdoptedSignature {
  signatureText: string
  signaturePng: string
  initialsPng?: string
}

interface AdoptSignatureModalProps {
  open: boolean
  defaultName: string
  needsInitials: boolean
  onClose: () => void
  onAdopt: (s: AdoptedSignature) => void
}

/** Mount fresh each time it opens; state is seeded from props once. */
export default function AdoptSignatureModal({ open, defaultName, needsInitials, onClose, onAdopt }: AdoptSignatureModalProps) {
  const [tab, setTab] = useState<'type' | 'draw'>('type')
  const [name, setName] = useState(defaultName)
  const [initials, setInitials] = useState(initialsFrom(defaultName))
  const [initialsTouched, setInitialsTouched] = useState(false)
  const [sigInk, setSigInk] = useState(false)
  const [iniInk, setIniInk] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const sigPad = useRef<SignaturePadHandle>(null)
  const iniPad = useRef<SignaturePadHandle>(null)

  function changeName(v: string) {
    setName(v)
    if (!initialsTouched) setInitials(initialsFrom(v))
  }

  const ready = name.trim().length > 0 && (tab === 'type'
    ? !needsInitials || initials.trim().length > 0
    : sigInk && (!needsInitials || iniInk))

  async function adopt() {
    setError('')
    setBusy(true)
    try {
      const signatureText = name.trim()
      if (tab === 'type') {
        onAdopt({
          signatureText,
          signaturePng: await typedToPng(signatureText),
          initialsPng: needsInitials ? await typedToPng(initials.trim(), 100, 300) : undefined,
        })
      } else {
        onAdopt({
          signatureText,
          signaturePng: sigPad.current!.toPng(),
          initialsPng: needsInitials ? iniPad.current!.toPng() : undefined,
        })
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the signature image.')
    } finally {
      setBusy(false)
    }
  }

  const tabBtn = (t: 'type' | 'draw', label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === t}
      onClick={() => setTab(t)}
      style={{
        flex: 1, padding: `${spacing.sm} 0`, background: 'none', cursor: 'pointer',
        border: 'none', borderBottom: `2px solid ${tab === t ? colors.primary : 'transparent'}`,
        color: tab === t ? colors.primary : colors.textSecondary, fontSize: fontSize.sm, fontWeight: 600,
      }}
    >
      {label}
    </button>
  )

  const preview = (text: string, size: number, placeholder: string) => (
    <div style={{
      border: `1px dashed ${colors.border}`, borderRadius: radius.md, background: '#fff',
      padding: `${spacing.sm} ${spacing.md}`, minHeight: size * 1.6, display: 'flex', alignItems: 'center',
      fontFamily: SIGNATURE_FONT, fontSize: size, color: text ? INK : colors.textMuted,
      overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
    }}>
      {text || placeholder}
    </div>
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Adopt your signature"
      subtitle="This is how your signature will appear on the document."
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={adopt} disabled={!ready} loading={busy}>Adopt and continue</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <Input label="Full legal name" aria-label="Full legal name" value={name} onChange={e => changeName(e.target.value)} autoComplete="name" />
        <div role="tablist" style={{ display: 'flex', borderBottom: `1px solid ${colors.border}` }}>
          {tabBtn('type', 'Type')}
          {tabBtn('draw', 'Draw')}
        </div>
        {tab === 'type' ? (
          <>
            <div>
              <div style={{ fontSize: fontSize.sm, fontWeight: 500, color: colors.text, marginBottom: 4 }}>Signature preview</div>
              {preview(name.trim(), 36, 'Your name')}
            </div>
            {needsInitials && (
              <>
                <Input
                  label="Initials"
                  aria-label="Initials"
                  value={initials}
                  maxLength={6}
                  onChange={e => { setInitials(e.target.value); setInitialsTouched(true) }}
                />
                {preview(initials.trim(), 30, 'AB')}
              </>
            )}
          </>
        ) : (
          <>
            <SignaturePad ref={sigPad} label="Draw your signature" onInkChange={setSigInk} />
            {needsInitials && (
              <div style={{ maxWidth: 260 }}>
                <SignaturePad ref={iniPad} label="Draw your initials" width={300} height={150} onInkChange={setIniInk} />
              </div>
            )}
          </>
        )}
        {error && <div style={{ color: colors.error, fontSize: fontSize.sm }}>{error}</div>}
      </div>
    </Modal>
  )
}
